// Auth wrappers around Better Auth client. The exported function shapes
// (`{ data, error }` envelope, error.message strings translated by
// auth-errors.ts) match what useAuthForm and the auth components
// already expect, so the migration off Supabase doesn't require
// touching every caller.

import { authClient } from "./auth-client";

export async function resetPassword(email: string, redirectTo?: string) {
  try {
    const { data, error } = await authClient.requestPasswordReset({
      email,
      redirectTo: redirectTo ?? `${window.location.origin}/auth/confirm`,
    });
    if (error) {
      return { data: null, error: new Error(error.message ?? "Reset failed") };
    }
    return { data, error: null };
  } catch (e: any) {
    return {
      data: null,
      error: new Error(e?.message ?? "Parooli lähtestamine ebaõnnestus"),
    };
  }
}

export async function signIn(email: string, password: string) {
  try {
    const { data, error } = await authClient.signIn.email({ email, password });
    if (error) {
      return { data: null, error: new Error(error.message ?? "Sign in failed") };
    }
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: e };
  }
}

export async function signUp(
  email: string,
  password: string,
  fullName: string,
  options?: { redirectTo?: string }
) {
  try {
    // callbackURL must be absolute — Better Auth resolves relative URLs
    // against BETTER_AUTH_URL (the API host), which has no SPA routes
    // and would 404. Always land on EmailConfirmSuccess on the frontend
    // origin; if the caller wants a post-verify destination (e.g. the
    // checkout flow), forward it via ?redirect_to= so the success page
    // can read it from searchParams.
    const origin = window.location.origin;
    const successUrl = `${origin}/email-confirm-success`;
    const callbackURL = options?.redirectTo
      ? `${successUrl}?redirect_to=${encodeURIComponent(options.redirectTo)}`
      : successUrl;
    const { data, error } = await authClient.signUp.email({
      email,
      password,
      name: fullName,
      callbackURL,
    });
    if (error) {
      return { data: null, error: new Error(error.message ?? "Sign up failed") };
    }
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: e };
  }
}

export async function signOut() {
  try {
    const { error } = await authClient.signOut();
    if (error) {
      return { error: new Error(error.message ?? "Sign out failed") };
    }
    return { error: null };
  } catch (e: any) {
    return { error: e };
  }
}

// Reset-password completion using the token emailed to the user.
// The currently-logged-in change-password flow lives in the profile
// PasswordChangeForm, which calls authClient.changePassword directly.
export async function updatePassword(newPassword: string, token?: string) {
  try {
    if (!token) {
      return {
        data: null,
        error: new Error("Reset token puudub. Palun klõpsa uuesti e-kirjas oleval lingil."),
      };
    }
    const { data, error } = await authClient.resetPassword({
      newPassword,
      token,
    });
    if (error) {
      return { data: null, error: new Error(error.message ?? "Reset failed") };
    }
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: e };
  }
}
